<template>
  <div class="bg-white rounded-2xl p-3.5 sm:p-5 shadow-sm border border-gray-100 flex flex-col gap-3">
    <!-- Top Row: Header & Date Chip -->
    <div class="flex items-center justify-between gap-2">
      <div class="flex items-center gap-2">
        <v-avatar size="32" color="indigo-50" class="shrink-0">
          <v-icon small color="indigo">mdi-clock-outline</v-icon>
        </v-avatar>
        <h3 class="text-sm sm:text-base font-bold text-gray-900 mb-0 leading-tight">Tracker</h3>
      </div>

      <!-- Date Chip -->
      <div
        class="bg-gray-50 text-gray-600 font-semibold text-[11px] sm:text-xs px-2.5 py-1 rounded-full border border-gray-200/70 flex items-center gap-1 shrink-0">
        <v-icon x-small color="indigo">mdi-calendar-range</v-icon>
        <span>{{ todayFormatted }}</span>
      </div>
    </div>

    <!-- Timer / Status Counters Full Width -->
    <div class="grid grid-cols-2 gap-2 sm:gap-3 w-full">
      <div
        class="bg-gray-50/90 p-2 sm:p-2.5 rounded-xl border border-gray-100 flex flex-col items-center justify-center text-center">
        <span class="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Work Time</span>
        <span class="text-base sm:text-lg font-black text-gray-800 tracking-tight font-mono">
          {{ formattedWorkTime }}
        </span>
      </div>

      <!-- Status / Active Activity Card (Replaces Break Time counter) -->
      <div
        :class="activeActivity ? 'bg-amber-50/80 border-amber-200' : 'bg-gray-50/90 border-gray-100'"
        class="p-2 sm:p-2.5 rounded-xl border flex flex-col items-center justify-center text-center transition-all min-w-0">
        <span
          :class="activeActivity ? 'text-amber-800' : 'text-gray-400'"
          class="text-[10px] font-bold uppercase tracking-wider mb-0.5">
          {{ activeActivity ? 'Active Activity' : 'Status' }}
        </span>

        <div v-if="activeActivity" class="flex items-center gap-1.5 w-full justify-center">
          <span class="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0"></span>
          <span class="text-xs sm:text-sm font-black text-amber-900 truncate">
            {{ activeActivity }}
          </span>
        </div>
        <div v-else class="flex items-center gap-1.5">
          <span class="w-2 h-2 rounded-full" :class="isCheckedIn ? 'bg-emerald-500' : 'bg-gray-400'"></span>
          <span class="text-xs sm:text-sm font-bold text-gray-700">
            {{ isCheckedIn ? 'On Duty' : 'Clocked Out' }}
          </span>
        </div>
      </div>
    </div>

    <!-- Quick Custom Activity Buttons when Clocked In -->
    <div v-if="isCheckedIn" class="flex flex-col gap-2 pt-2 border-t border-dashed border-gray-100 w-full">
      <!-- Active Activity Banner when an activity is in progress -->
      <div
        v-if="activeActivity"
        class="w-full bg-amber-50 border border-amber-200 rounded-xl p-2 sm:p-2.5 flex items-center justify-between gap-2 shadow-2xs">
        <div class="flex items-center gap-2 min-w-0">
          <span class="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse shrink-0"></span>
          <div class="flex flex-col min-w-0 text-left">
            <span class="text-[9px] font-bold text-amber-700 uppercase tracking-wider leading-none">In Progress</span>
            <span class="text-xs font-black text-amber-950 truncate leading-tight mt-0.5">{{ activeActivity }}</span>
          </div>
        </div>
        <v-btn
          depressed
          small
          color="amber darken-2"
          :loading="activityLoading"
          class="white--text font-extrabold text-xs rounded-lg px-3 h-7 capitalize shrink-0 shadow-none"
          @click="endCustomActivity(activeActivity)">
          <v-icon left x-small>mdi-stop-circle</v-icon>
          End {{ activeActivity }}
        </v-btn>
      </div>

      <!-- Action Buttons Row (Changes to "End {{custom activity}}" for the active one) -->
      <div class="flex items-center gap-1.5 overflow-x-auto no-scrollbar w-full">
        <button
          v-for="step in customStepsList"
          :key="step"
          type="button"
          :disabled="activityLoading || (activeActivity && activeActivity !== step)"
          @click="handleActivityClick(step)"
          :class="activeActivity === step
            ? 'bg-amber-500 text-white font-bold border-amber-500 shadow-xs'
            : (activeActivity ? 'bg-gray-50/50 text-gray-400 border-gray-100 cursor-not-allowed opacity-60' : 'bg-gray-50/80 text-gray-700 hover:bg-gray-100 border-gray-200/80')"
          class="flex-1 flex items-center justify-center gap-1 py-1 px-1.5 rounded-lg text-[11px] font-semibold border border-solid transition-all cursor-pointer select-none min-w-0 h-8">
          <v-icon x-small :color="activeActivity === step ? 'white' : '#6b7280'" class="shrink-0">
            {{ activeActivity === step ? 'mdi-stop-circle-outline' : getActivityIcon(step) }}
          </v-icon>
          <span class="truncate leading-none">
            {{ activeActivity === step ? `End ${step}` : step }}
          </span>
        </button>
      </div>
    </div>

    <!-- Primary Clock In / Out & Mobile Menu Action Row (75% Clock In/Out, 25% Menu on mobile) -->
    <div class="flex items-center gap-2 mt-0.5 w-full">
      <!-- 75% Width Clock In / Out Button on mobile (Full Width on Desktop) -->
      <v-btn :color="isCheckedIn ? 'error' : $vuetify.theme.currentTheme.employeePrimary" :loading="loading" depressed
        class="w-3/4 lg:w-full h-9 rounded-xl text-xs sm:text-sm font-extrabold white--text shadow-xs"
        @click="$emit('toggle-clock-in')">
        <v-icon left small>{{ isCheckedIn ? 'mdi-logout' : 'mdi-login' }}</v-icon>
        {{ isCheckedIn ? 'CLOCK OUT' : 'CLOCK IN' }}
      </v-btn>

      <!-- 25% Width Mobile Menu Button beside Clock In/Out (Visible on mobile screens only) -->
      <v-btn depressed color="indigo lighten-5"
        class="w-1/4 lg:hidden h-9 min-w-0 rounded-xl indigo--text text--darken-4 shrink-0 flex items-center justify-center p-0 border border-indigo-100 shadow-none"
        @click="$emit('open-mobile-menu')" title="Quick Menu & Activity">
        <v-icon small color="indigo darken-3">mdi-dots-grid</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { formatTimeSeconds } from '@/app/utils/helpers'
import { recordCustomActivity } from '@/app/utils/taskFlow'

export default {
  name: 'AppTimeTracker',
  props: {
    isCheckedIn: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    initialWorkSeconds: {
      type: Number,
      default: 0
    },
    workingHoursText: {
      type: String,
      default: ''
    },
    customActivitySteps: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      workSeconds: this.initialWorkSeconds || 0,
      timerInterval: null,
      activeActivity: null,
      activityLoading: false
    }
  },
  computed: {
    todayFormatted() {
      return dayjs().format('dddd, DD MMMM YYYY')
    },
    formattedWorkTime() {
      if (this.workSeconds > 0) {
        return formatTimeSeconds(this.workSeconds)
      }
      return this.workingHoursText || '00:00:00'
    },
    customStepsList() {
      if (this.customActivitySteps && Array.isArray(this.customActivitySteps) && this.customActivitySteps.length > 0) {
        const filtered = this.customActivitySteps
          .filter((s) => s && s.enabled !== false)
          .map((s) => (typeof s === 'string' ? s : s.step || s.name || ''))
          .filter(Boolean)
        if (filtered.length > 0) return filtered
      }
      return ['Lunch', 'Tea', 'Meeting']
    }
  },
  watch: {
    initialWorkSeconds(newVal) {
      if (newVal > 0) {
        this.workSeconds = newVal
      }
    },
    isCheckedIn(val) {
      if (val) {
        this.startTimer()
        this.restoreActiveActivity()
      } else {
        this.stopTimer()
        if (this.activeActivity) {
          this.endCustomActivity(this.activeActivity, false)
        }
      }
    }
  },
  mounted() {
    if (this.isCheckedIn) {
      this.startTimer()
      this.restoreActiveActivity()
    }
  },
  beforeDestroy() {
    this.stopTimer()
  },
  methods: {
    startTimer() {
      if (this.timerInterval) clearInterval(this.timerInterval)
      this.timerInterval = setInterval(() => {
        // Continuous work time tracking - break time is not accumulated
        this.workSeconds += 1
      }, 1000)
    },
    stopTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
        this.timerInterval = null
      }
    },
    restoreActiveActivity() {
      const saved = localStorage.getItem('active_custom_activity')
      if (saved) {
        this.activeActivity = saved
        return
      }
      const breakData = localStorage.getItem('user_breaks')
      if (breakData) {
        try {
          const parsed = JSON.parse(breakData)
          const keys = Object.keys(parsed)
          if (keys.length > 0) {
            this.activeActivity = keys[0]
          }
        } catch (e) {
          console.warn('Could not parse user_breaks from localStorage:', e)
        }
      }
    },
    getActivityIcon(step) {
      const s = (step || '').toLowerCase()
      if (s.includes('lunch') || s.includes('food') || s.includes('dinner') || s.includes('eat')) return 'mdi-food'
      if (s.includes('tea') || s.includes('coffee') || s.includes('break')) return 'mdi-coffee'
      if (s.includes('meet') || s.includes('client') || s.includes('call')) return 'mdi-account-group'
      if (s.includes('travel') || s.includes('drive')) return 'mdi-car'
      if (s.includes('fuel')) return 'mdi-gas-station'
      return 'mdi-clock-fast'
    },
    async handleActivityClick(step) {
      if (!this.isCheckedIn) {
        this.$toast?.warning?.('Please Clock In first to perform activities')
        return
      }
      if (this.activeActivity === step) {
        await this.endCustomActivity(step)
      } else {
        if (this.activeActivity) {
          await this.endCustomActivity(this.activeActivity, false)
        }
        await this.startCustomActivity(step)
      }
    },
    async startCustomActivity(step) {
      try {
        this.activityLoading = true
        const action = step.toLowerCase().replace(/\s+/g, '_')
        await recordCustomActivity(action)

        this.activeActivity = step
        localStorage.setItem('active_custom_activity', step)
        localStorage.setItem('user_breaks', JSON.stringify({ [step]: 1 }))

        this.$emit('custom-activity-started', step)
        this.$toast?.success?.(`Started ${step}`)
      } catch (err) {
        console.error('Failed to start custom activity:', err)
        this.$toast?.error?.(`Failed to start ${step}`)
      } finally {
        this.activityLoading = false
      }
    },
    async endCustomActivity(step, showToast = true) {
      try {
        this.activityLoading = true
        const action = `end_${step.toLowerCase().replace(/\s+/g, '_')}`
        await recordCustomActivity(action)

        this.activeActivity = null
        localStorage.removeItem('active_custom_activity')
        localStorage.removeItem('user_breaks')

        this.$emit('custom-activity-ended', step)
        if (showToast) {
          this.$toast?.success?.(`Ended ${step}`)
        }
      } catch (err) {
        console.error('Failed to end custom activity:', err)
        if (showToast) {
          this.$toast?.error?.(`Failed to end ${step}`)
        }
      } finally {
        this.activityLoading = false
      }
    }
  }
}
</script>
